import type { PioneerHoloEffect } from "@shared/lib/cardHolo";
import { cn } from "@shared/lib/utils";
import type { AgentCardItem } from "@/lib/api";
import { cardArtSrc } from "@/lib/cardArt";
import type { CardFinishMeta } from "@/lib/cards";
import { fmt } from "@/lib/format";
import type { HomeAgent } from "@/lib/homeAgents";
import { mockCardIp } from "@/lib/renewal/cardActions";
import { SUPPLY_TEXT, itemSupplyStatus } from "@/lib/supplyStatus";
import { supplyLine } from "./supply";

/**
 * Rarity aspiration while locked — rarity speaks violet here, never the
 * action green: mid rarities (radiant-holo) carry a faint rarity-violet
 * hairline, hyper-rare tiers a faint yellow one — no glow.
 */
const RARITY_RING: Record<PioneerHoloEffect, string> = {
  basic: "border-white/8",
  "rainbow-holo": "border-fuchsia-400/25",
  "radiant-holo": "border-rarity/25",
  "hyper-rare": "border-yellow/25",
};

type LockedTileProps = {
  agent: HomeAgent;
  meta: CardFinishMeta;
  item: AgentCardItem;
  /** Catalog № — slot keeps its place and number while unowned (§2C). */
  number: number;
};

/**
 * Unowned slot — dark portrait silhouette so the grid itself reads as a dex:
 * position + catalog № stay, base value stays (§1.4 value shown regardless
 * of ownership). Sold-out 1-of-1s read differently from "not collected".
 */
export function LockedTile({ agent, meta, item, number }: LockedTileProps) {
  const status = itemSupplyStatus(item);
  const supplyClass = SUPPLY_TEXT[status ?? "plentiful"];
  const soldOut = status === "soldout";

  return (
    <article
      className="flex flex-col gap-2"
      aria-label={`${meta.label} — ${soldOut ? "sold out" : "not collected"}`}
    >
      <div
        className={cn(
          // PioneerHoloCard --card-radius(4.5%/3.5%)와 동일 반경 — owned/locked 타일 모서리 일치
          "relative aspect-[723/1024] overflow-hidden rounded-[4.5%/3.5%] border bg-white/[0.03]",
          RARITY_RING[meta.holoEffect],
        )}
      >
        {/* 실카드 아트 우선 — 미보유 명도 처리(opacity+grayscale)는 그대로 유지 */}
        <img
          src={cardArtSrc(agent.agentId, meta.finish) ?? agent.portrait}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          className="size-full object-cover opacity-20 grayscale"
        />
        <p
          className={cn(
            "absolute inset-x-0 bottom-2.5 px-2 text-center font-mono text-[9px] font-medium uppercase tracking-[0.14em]",
            soldOut ? "text-danger" : "text-white/55",
          )}
        >
          {soldOut ? "Sold out" : "Not collected"}
        </p>
      </div>

      {/* Courtyard tile grammar (§2): name 2 lines 14/700 → divider → value + supply chip */}
      <div className="px-0.5">
        <p className="line-clamp-2 min-h-10 text-sm font-bold leading-5 text-ink-dim">
          <span className="font-mono text-[10px] font-medium tabular-nums text-ink-mute">
            #{String(number).padStart(2, "0")}
          </span>{" "}
          {meta.label}
        </p>
        <div className="mt-1.5 border-t border-line" aria-hidden />
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <p className="font-mono text-xs font-medium tabular-nums text-ink-dim">
            {fmt(mockCardIp(meta.finish))} <span className="font-normal text-ink-mute">IP</span>
          </p>
          <span
            className={cn(
              "shrink-0 rounded-full border border-line bg-white/4 px-2 py-0.5 font-mono text-[10px] font-medium tabular-nums",
              supplyClass,
            )}
          >
            {soldOut ? "0 remaining" : supplyLine(meta, item)}
          </span>
        </div>
      </div>
    </article>
  );
}
